/**
 * Profile Panel Component
 * 
 * Side panel for user profile and settings.
 * Displays real billing data from backend APIs.
 */

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { getAppConfig } from "../../services/appConfigApi";
import { getSubscription, getPaymentHistory, type PaymentHistoryItem } from "../../services/billingApi";
import { createTicket } from "../../services/supportApi";
import { logout } from "../../services/authApi";
import { logger } from "../../services/logger";
// Styles are in styles.css (profile-panel classes)

type ProfilePanelProps = {
  isOpen: boolean;
  onClose: () => void;
  language: string;
};

export function ProfilePanel({ isOpen, onClose, language }: ProfilePanelProps): JSX.Element {
  const { user } = useAuth();
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  // Fetch app config (plan_code, role, trading_enabled)
  const { data: appConfig, isLoading: configLoading } = useQuery({
    queryKey: ["appConfig"],
    queryFn: getAppConfig,
    enabled: isOpen,
  });

  // Fetch subscription data
  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: getSubscription,
    enabled: isOpen,
  });

  // Fetch payment history (last 10)
  const { data: paymentHistory, isLoading: historyLoading } = useQuery({
    queryKey: ["paymentHistory"],
    queryFn: getPaymentHistory,
    enabled: isOpen,
  });

  const handleRequestEmailChange = async () => {
    setIsCreatingTicket(true);
    try {
      await createTicket({
        subject: "Request Email Change",
        message: `I would like to change my email address from ${user?.email || "current email"} to a new address. Please assist with this request.`,
        priority: "normal",
      });
      alert("Support ticket created successfully. We'll contact you soon.");
    } catch (error) {
      logger.error("Failed to create support ticket", error instanceof Error ? error : new Error(String(error)));
      alert("Failed to create support ticket. Please try again or contact support directly.");
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const handleOpenSupport = () => {
    window.location.href = "/support";
  };

  const getPlanDisplayName = (code: string | null | undefined): string => {
    if (!code) return "No plan";
    if (code === "STANDARD") return "Pro";
    if (code === "PRO") return "Ultra";
    return code;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const recentPayments = paymentHistory?.payments?.slice(0, 10) || [];
  const isAdmin = appConfig?.role === "admin";
  const planCode = appConfig?.plan_code;
  const subscription = subscriptionData?.subscription;

  return (
    <div className={isOpen ? "profile-panel profile-panel--open" : "profile-panel"}>
      <div className="profile-panel__backdrop" role="presentation" onClick={onClose} />
      <aside className="profile-panel__content" role="dialog" aria-modal="true" aria-labelledby="profile-panel-title">
        <header className="profile-panel__header">
          <div>
            <h2 id="profile-panel-title">Profile & Settings</h2>
            <p>Manage account preferences, security, and billing details.</p>
          </div>
          <button type="button" className="profile-panel__close" onClick={onClose} aria-label="Close profile panel">
            ✕
          </button>
        </header>

        {/* Personal Information */}
        <section className="profile-panel__section">
          <h3>Personal information</h3>
          <div className="profile-panel__form">
            <div className="profile-panel__field">
              <label>Display name</label>
              <div className="profile-panel__readonly">{user?.name || "N/A"}</div>
              <p className="profile-panel__field-hint">Name from Auth0 account</p>
            </div>
            <div className="profile-panel__field">
              <label>Email address</label>
              <div className="profile-panel__readonly">{user?.email || "N/A"}</div>
              <p className="profile-panel__field-hint">Email managed by Auth0</p>
              {isAdmin ? (
                <div className="profile-panel__admin-note">
                  <p>Admin: Change email in Auth0 Dashboard</p>
                  <a
                    href="https://manage.auth0.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="profile-panel__link"
                  >
                    Open Auth0 Dashboard →
                  </a>
                </div>
              ) : (
                <button
                  type="button"
                  className="profile-panel__link"
                  onClick={handleRequestEmailChange}
                  disabled={isCreatingTicket}
                >
                  {isCreatingTicket ? "Creating ticket..." : "Request email change"}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Security */}
        <section className="profile-panel__section">
          <h3>Security</h3>
          <div className="profile-panel__security">
            <div>
              <strong>Password</strong>
              <p>Password is managed by Auth0. Change it in your Auth0 account settings.</p>
              <button type="button" className="profile-panel__link" disabled>
                Coming soon
              </button>
            </div>
            <div>
              <strong>Two-factor authentication</strong>
              <p>Enable OTP or hardware key to protect access.</p>
              <button type="button" className="profile-panel__link" disabled>
                Coming soon
              </button>
            </div>
            <div>
              <strong>Active sessions</strong>
              <p>Session management will be available after identity provider integration.</p>
              <button type="button" className="profile-panel__link" disabled>
                Coming soon
              </button>
            </div>
          </div>
        </section>

        {/* Billing & Subscriptions */}
        <section className="profile-panel__section">
          <h3>Billing & subscriptions</h3>
          <div className="profile-panel__billing">
            <div>
              <span className="profile-panel__badge">Current plan</span>
              {configLoading ? (
                <div className="profile-panel__loading">Loading...</div>
              ) : (
                <>
                  <strong>{getPlanDisplayName(planCode)}</strong>
                  {subscriptionLoading ? (
                    <p>Loading subscription details...</p>
                  ) : subscription ? (
                    <>
                      <p>Status: {subscription.status}</p>
                      <div className="profile-panel__plan-meta">
                        <span>Started: {formatDate(subscription.starts_at)}</span>
                        {subscription.ends_at && (
                          <span>Renews: {formatDate(subscription.ends_at)}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p>No active subscription</p>
                  )}
                  <div className="profile-panel__plan-meta">
                    {appConfig && (
                      <>
                        <span>Trading enabled: {appConfig.user_trading_enabled ? "Yes" : "No"}</span>
                        {isAdmin && <span>Role: {appConfig.role}</span>}
                      </>
                    )}
                  </div>
                  <div className="profile-panel__actions">
                    {subscription && subscription.plan?.code && subscription.ends_at ? (
                      <>
                        <a
                          href={`/subscribe?plan=${subscription.plan.code}&cycle=${(() => {
                            // Calculate period from subscription duration
                            const startsAt = new Date(subscription.starts_at);
                            const endsAt = new Date(subscription.ends_at);
                            const daysDiff = Math.floor((endsAt.getTime() - startsAt.getTime()) / (1000 * 60 * 60 * 24));
                            // If duration is around 30 days, it's monthly; if around 365 days, it's yearly
                            return daysDiff <= 35 ? "monthly" : "yearly";
                          })()}`}
                          className="profile-panel__button profile-panel__button--primary"
                        >
                          Renew subscription
                        </a>
                        <a href="/subscribe" className="profile-panel__button">
                          Manage subscription
                        </a>
                      </>
                    ) : (
                      <a href="/subscribe" className="profile-panel__button">
                        {subscription ? "Manage subscription" : "Subscribe"}
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Payment History */}
        {recentPayments.length > 0 && (
          <section className="profile-panel__section">
            <h3>Recent payments</h3>
            <div className="profile-panel__payment-history">
              {historyLoading ? (
                <div className="profile-panel__loading">Loading payment history...</div>
              ) : (
                <div className="profile-panel__payment-list">
                  {recentPayments.map((payment: PaymentHistoryItem) => (
                    <div key={payment.id} className="profile-panel__payment-item">
                      <div className="profile-panel__payment-main">
                        <span className="profile-panel__payment-amount">{formatCurrency(payment.amount_usd)}</span>
                        <span className="profile-panel__payment-method">{payment.method}</span>
                        <span className={`profile-panel__payment-status profile-panel__payment-status--${payment.status.toLowerCase()}`}>
                          {payment.status}
                        </span>
                      </div>
                      <div className="profile-panel__payment-meta">
                        <span>{formatDate(payment.created_at)}</span>
                        {payment.tx_hash && (
                          <span className="profile-panel__payment-tx">
                            TX: {payment.tx_hash.slice(0, 8)}...
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Reporting Shortcuts */}
        <section className="profile-panel__section">
          <h3>Reporting shortcuts</h3>
          <div className="profile-panel__reports">
            <button type="button" className="profile-panel__button" disabled>
              Request performance statement
            </button>
            <button type="button" className="profile-panel__button" disabled>
              Export billing history
            </button>
            <button type="button" className="profile-panel__button" disabled>
              Download AI activity log
            </button>
            <p className="profile-panel__coming-soon">Coming soon</p>
          </div>
        </section>

        {/* Preferences */}
        <section className="profile-panel__section">
          <h3>Preferences</h3>
          <div className="profile-panel__preferences">
            <div>
              <span>Interface language</span>
              <strong>{language === "ar" ? "Arabic" : "English"}</strong>
              <p>Language selection persists across sessions once profile APIs are ready.</p>
            </div>
            <div>
              <span>Notification policy</span>
              <p>Configure trading alerts, AI summaries, and billing notifications after messaging service connection.</p>
            </div>
          </div>
        </section>

        {/* Logout */}
        <section className="profile-panel__section profile-panel__section--logout">
          <button
            type="button"
            className="profile-panel__button profile-panel__button--logout"
            onClick={() => {
              logout();
            }}
            aria-label="Log out of your account"
          >
            Log out
          </button>
        </section>
      </aside>
    </div>
  );
}
